/**
 * FreeScorePopup — /free-score dwell + exit-intent capture.
 *
 * Behaviour:
 *   - Triggers after 5 seconds on the page OR on mouse exit intent
 *     (cursor leaving the top of the viewport while moving upward).
 *   - Whichever fires first wins.
 *   - Once dismissed OR submitted, it is not shown again for the rest
 *     of the tab session (sessionStorage flag).
 *
 * Fields captured:
 *   - Email (required)
 *   - LinkedIn URL (required, must contain linkedin.com/in/)
 *   - Primary goal (3 chips — matches /free giveaway taxonomy)
 *
 * On submit → POST /api/free-giveaway/request with source="free-score-popup"
 * so the row lands in the same admin panel as /free page leads (no
 * separate collection needed).
 *
 * Success state renders inside the popup for 3 seconds, then auto-closes.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Gift, ArrowRight, Loader2, Check, Briefcase, Rocket, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { trackLead } from '../../lib/analytics';

const STORAGE_KEY = 'blayzrr-freescore-popup-shown';
const DWELL_MS = 5000;

const GOALS = [
  {
    key: 'recruiter_visibility',
    label: 'Get noticed by recruiters',
    subtitle: 'Improve search visibility and communicate your professional value.',
    Icon: Briefcase,
  },
  {
    key: 'thought_leadership',
    label: 'Build thought leadership',
    subtitle: 'Establish authority and make your expertise more memorable.',
    Icon: Rocket,
  },
  {
    key: 'business_development',
    label: 'Win clients and business opportunities',
    subtitle: 'Position yourself clearly for prospects, partners and decision-makers.',
    Icon: TrendingUp,
  },
];

function isValidLinkedInUrl(url) {
  const u = (url || '').trim().toLowerCase();
  return u.includes('linkedin.com/in/');
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
}

export default function FreeScorePopup() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [goal, setGoal] = useState('recruiter_visibility');
  const triggeredRef = useRef(false);
  const closeTimerRef = useRef(null);

  // ---- Trigger logic: 5s dwell OR exit intent (whichever fires first) ----
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') return undefined;
    } catch (_) { /* sessionStorage disabled — proceed */ }

    const trigger = () => {
      if (triggeredRef.current) return;
      triggeredRef.current = true;
      setOpen(true);
    };

    const dwellTimer = setTimeout(trigger, DWELL_MS);

    const handleMouseOut = (e) => {
      if (triggeredRef.current) return;
      // Exit-intent = cursor leaves the top edge (relatedTarget null +
      // clientY at 0 or negative). Ignores side/bottom exits which
      // typically indicate scrolling / iframe reads, not intent to leave.
      if (!e.relatedTarget && (e.clientY <= 0 || e.pageY <= 0)) {
        trigger();
      }
    };
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      clearTimeout(dwellTimer);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, []);

  // Lock body scroll while open so the fixed backdrop feels modal.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Once open state changes, mark shown in sessionStorage so the popup
  // never re-triggers on this tab.
  useEffect(() => {
    if (!open) return;
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (_) { /* noop */ }
  }, [open]);

  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);

  const close = () => {
    setOpen(false);
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');

    if (!isValidEmail(email)) { setError('Enter a valid email address.'); return; }
    if (!isValidLinkedInUrl(linkedin)) {
      setError('Enter a valid LinkedIn profile URL (must include linkedin.com/in/…).');
      return;
    }

    setSubmitting(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${base}/api/free-giveaway/request`, {
        email: email.trim(),
        linkedin_url: linkedin.trim(),
        goal,
        source: 'free-score-popup',
        consent: true,
      });
      setSubmitted(true);
      trackLead('free_score_popup', { goal });
      // Auto-close after 3 seconds so the visitor returns to the page.
      closeTimerRef.current = setTimeout(() => setOpen(false), 3000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object'
        ? (detail?.message || 'Something went wrong. Please try again.')
        : (detail || 'Something went wrong. Please try again.');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fs-popup-title"
      data-testid="free-score-popup"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        data-testid="free-score-popup-backdrop"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-[540px] rounded-3xl bg-white dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 shadow-2xl p-6 sm:p-8 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          aria-label="Close popup"
          data-testid="free-score-popup-close"
        >
          <X size={16} />
        </button>

        {submitted ? (
          <SuccessState onClose={close} />
        ) : (
          <>
            {/* 100% FREE pill */}
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/25 px-3.5 py-1.5">
              <Gift size={13} className="text-emerald-600 dark:text-emerald-400" />
              <span className="text-[12.5px] font-semibold text-emerald-700 dark:text-emerald-300">
                100% FREE
              </span>
              <span className="text-[12px] text-emerald-600/80 dark:text-emerald-400/80">· No credit card</span>
            </div>

            <p className="mt-4 text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-primary">
              Your profile & goal
            </p>
            <h2
              id="fs-popup-title"
              className="mt-1.5 text-2xl sm:text-3xl font-bold leading-tight text-slate-900 dark:text-slate-50"
            >
              Get a done-for-you LinkedIn makeover — free.
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-slate-600 dark:text-slate-300">
              Paste your LinkedIn URL and email — we&rsquo;ll audit, rewrite and email the whole profile back to you within 24 hours. No cost, no catch.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="free-score-popup-form">
              {/* Email */}
              <div>
                <label
                  htmlFor="fs-popup-email"
                  className="block text-[13px] font-semibold text-slate-800 dark:text-slate-100 mb-1.5"
                >
                  Your email
                </label>
                <input
                  id="fs-popup-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-[14.5px] text-slate-900 dark:text-slate-50 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 transition-colors"
                  required
                  data-testid="free-score-popup-email"
                />
              </div>

              {/* LinkedIn URL */}
              <div>
                <label
                  htmlFor="fs-popup-linkedin"
                  className="block text-[13px] font-semibold text-slate-800 dark:text-slate-100 mb-1.5"
                >
                  Your LinkedIn profile URL
                </label>
                <input
                  id="fs-popup-linkedin"
                  type="url"
                  value={linkedin}
                  onChange={(e) => setLinkedin(e.target.value)}
                  placeholder="https://linkedin.com/in/your-handle"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-[14.5px] text-slate-900 dark:text-slate-50 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 transition-colors"
                  required
                  data-testid="free-score-popup-linkedin"
                />
              </div>

              {/* Primary goal */}
              <div>
                <p className="block text-[13px] font-semibold text-slate-800 dark:text-slate-100 mb-2">
                  Your primary goal
                </p>
                <div className="space-y-2" role="radiogroup" aria-label="Primary goal">
                  {GOALS.map(({ key, label, subtitle, Icon }) => {
                    const active = goal === key;
                    return (
                      <button
                        type="button"
                        key={key}
                        role="radio"
                        aria-checked={active}
                        onClick={() => setGoal(key)}
                        className={`w-full text-left rounded-xl border px-3.5 py-3 flex items-start gap-3 transition-all ${
                          active
                            ? 'border-primary bg-primary/[0.05] shadow-sm'
                            : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700'
                        }`}
                        data-testid={`free-score-popup-goal-${key}`}
                      >
                        <span className={`shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg ${
                          active ? 'bg-primary text-primary-foreground' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                        }`}>
                          <Icon size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[14px] font-semibold text-slate-900 dark:text-slate-50 leading-tight">
                            {label}
                          </span>
                          <span className="block text-[12.5px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                            {subtitle}
                          </span>
                        </span>
                        {active && (
                          <span className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground mt-0.5">
                            <Check size={11} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {error && (
                <p className="text-[13px] text-red-600 dark:text-red-400" data-testid="free-score-popup-error">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-xl bg-amber-400 hover:bg-amber-500 text-slate-900 font-semibold text-[15px] shadow-md hover:shadow-lg transition-all disabled:opacity-70"
                data-testid="free-score-popup-submit"
              >
                {submitting ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Submitting…</>
                ) : (
                  <>Submit <ArrowRight size={16} className="ml-2" /></>
                )}
              </Button>

              <p className="text-[11.5px] text-center text-slate-500 dark:text-slate-400 leading-relaxed">
                Fully audited, rewritten LinkedIn profile — emailed within 24 hours.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function SuccessState({ onClose }) {
  return (
    <div className="py-4 sm:py-6 text-center" data-testid="free-score-popup-success">
      <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/12 text-emerald-600 dark:text-emerald-400">
        <Check size={26} />
      </div>
      <h2 className="mt-4 text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">
        Got it — your audit is on the way.
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-slate-600 dark:text-slate-300 max-w-[42ch] mx-auto">
        Our LinkedIn experts will send you a fully rewritten profile within <span className="font-semibold text-slate-800 dark:text-slate-100">24 hours</span>. Check your inbox (and spam, just in case).
      </p>
      <p className="mt-4 text-[11.5px] text-slate-500 dark:text-slate-400">
        This will close in a moment…
      </p>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        className="mt-3 rounded-full text-[13px]"
        data-testid="free-score-popup-success-close"
      >
        Close now
      </Button>
    </div>
  );
}
